import { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { toast } from 'sonner';
import { usePaymentSettings } from '@/hooks/usePaymentSettings';

interface ProtectedOnboardedRouteProps {
  readonly children?: React.ReactNode;
}

/**
 * Route guard that only allows organisations with completed PaySuite onboarding through.
 *
 * This guard must always be nested inside `ProtectedAdminRoute` because the payment settings
 * endpoint is admin-only. It fails closed when onboarding state cannot be read or is incomplete.
 */
export function ProtectedOnboardedRoute({ children }: ProtectedOnboardedRouteProps) {
  const { data: settings, isLoading, isError } = usePaymentSettings({ enabled: true, forceLive: true });
  const toastShownRef = useRef(false);
  const isUnresolved = !isError && settings === undefined;
  const isDenied = !isLoading && !isUnresolved && (isError || settings?.onboardingCompleted !== true);

  useEffect(() => {
    if (isDenied && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.error('Complete Payment Links onboarding first');
    }
  }, [isDenied]);

  if (isLoading || isUnresolved) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }

  if (isDenied) {
    return <Navigate to="/payments/onboarding" replace />;
  }

  return <>{children ?? <Outlet />}</>;
}
